(ns ui.view.entry-criteria.target-picker
  "Modal content for setting a missing target on an opportunity before the
   stage-move can proceed. Triggered from
   `frontend.view.entity/attempt-move-to-stage!` when the opportunity has no
   target and its pipeline disallows target-less opportunities.

   Adapted from `ui.view.entity-creator.opportunity.view/select-target-view`:
   same search-selector + create-buttons UX, but rewritten without re-frame
   subscriptions/dispatches. State is local reagent atoms; the only inputs are
   the opportunity / pipeline and the parent-supplied callbacks.

   For investor-target-profile pipelines (froppo, exit-oppo, sales-oppo on
   investor-only) the picker surfaces person/company as pickable \"vehicle
   types\" for the underlying legal entity. Whether the user picks an
   existing legal entity or creates a fresh one, the target-attribute write
   sends a legal-entity id; the backend's `ref-normalization` wraps it in an
   investor (reusing the existing active one when present, otherwise
   creating a new one).

   Hosted in `frontend.view.modals.dynamic.view` so we don't have to register
   a fresh modal key.

   See docs/functional/stage_entry_criteria/."
  (:require [common.entity :as entity]
            [common.localization :as loc]
            [common.schema.entities.pipeline :as pipeline]
            [common.schema.entity-type-names :as etn]
            [common.util :as cu]
            [frontend.actions.entity :as entity-actions]
            [frontend.state.current-session :as session]
            [frontend.view.modals.dynamic.view :as dynamic-modal]
            [frontend.view.modals.events :as modals]
            [reagent.core :as r]
            [ui.components.button.view :as button]
            [ui.components.search-selector.view :as search-selector]
            [ui.editors.labelled-input.view :as labelled-input]
            [ui.util :as uu]))

(defn result-match-string
  "What we compare a typed query against for the duplicate-detection in
   `initial-query-for-create`. For person we use the universal email (names
   are common, emails are the unique anchor); for everything else we use
   `entity/display-string` so the comparison naturally tracks whichever
   attribute the entity-type considers its display name (`:asset/name` for
   asset types, `:oppo/name` for deals, etc.)."
  [entity-type original]
  (if (= entity-type :entity.type/person)
    (:person/universal-email-address original)
    (entity/display-string (assoc original :entity/type entity-type))))

(defn initial-query-for-create
  "Mirrors the opportunity-creator's `select-target-view`: returns `query`
   only when it's non-blank and no row in `results` matches it for this
   entity-type. Returns nil otherwise — caller passes nil through as
   `:start-value` so the creator's name field opens empty, nudging the user
   toward picking the existing entity instead of creating a duplicate."
  [entity-type query results]
  (let [lc-query (cu/str-trim-lcase query)]
    (when (and (not (cu/blank-value? query))
               (not (some (fn [{:keys [_source]}]
                            (= lc-query
                               (cu/str-trim-lcase
                                (result-match-string
                                 entity-type (:original _source)))))
                          results)))
      query)))

(def create-button-loc-keys
  "Per-entity-type loc key for the `+ New …` button label. Keys are added
   here as we support more target asset types."
  {:entity.type/person :new-contact
   :entity.type/company :new-company
   :entity.type/real-estate :new-real-estate
   :entity.type/fof-asset :new-fund
   :entity.type/other-asset :new-generic-asset})

(def no-target-loc-keys
  "Per-opportunity-type loc-keys for the no-target picker's title, type-label
   (shown above the type-selector when the pipeline accepts multiple
   target-asset-types), and search-label. Localization handles article and
   word-order (\"an investor\" vs \"a target asset\") instead of caller-side
   grammar — keep it that way: add a new entry here when a new
   opportunity-type needs picker support rather than synthesizing the string
   at runtime."
  {:entity.type/oppo
   {:title :entry-criteria/no-target-title-oppo
    :type-label :entry-criteria/no-target-type-label-oppo
    :search-label :entry-criteria/no-target-search-label-oppo}
   :entity.type/fundraising-opportunity
   {:title :entry-criteria/no-target-title-fundraising-opportunity
    :type-label :entry-criteria/no-target-type-label-fundraising-opportunity
    :search-label :entry-criteria/no-target-search-label-fundraising-opportunity}
   :entity.type/sales-opportunity
   {:title :entry-criteria/no-target-title-sales-opportunity
    :type-label :entry-criteria/no-target-type-label-sales-opportunity
    :search-label :entry-criteria/no-target-search-label-sales-opportunity}
   :entity.type/exit-opportunity
   {:title :entry-criteria/no-target-title-exit-opportunity
    :type-label :entry-criteria/no-target-type-label-exit-opportunity
    :search-label :entry-criteria/no-target-search-label-exit-opportunity}})

(defn result-item
  [{:keys [result on-select]}]
  (let [{:keys [_source]} result
        original (:original _source)
        et (-> original :entity/type keyword)
        et-name (etn/get-name et :en)
        entity (assoc original :entity/type et)
        icon (entity/default-icon entity)
        email (:person/universal-email-address original)
        title (entity/display-string entity)]
    [:div
     {:class (uu/cm "flex flex-col px-4 py-2"
                    "hover:bg-base-regular hover:cursor-pointer")
      :on-click (fn [_] (on-select (:db/id original)))}
     [:div {:class "font-semibold pb-1"} title]
     [:div
      {:class "flex gap-1 text-base-regular-text-paler text-sm m-0"}
      [:i {:class (uu/cm "fa fal" icon)}]
      [:div et-name]
      (when email
        [:div " · " email])]]))

(defn selected-card
  "Full-width single-line card displaying the currently-selected target,
   replacing the search-selector row when a selection exists. The X icon on
   the right clears the selection so the user can search again. Single-line
   (icon + `entity/display-string :long`) so the row height matches the
   searchbox — keeps modal-height stable across searching / selected states.
   No `+ Add another` affordance: the card reads as one current value, not a
   multi-select chip."
  [{:keys [entity on-clear!]}]
  (let [icon (entity/default-icon entity)
        title (entity/display-string entity :long)]
    ;; py-[13px] (vs py-3 = 12px) matches the searchbox row height, which is
    ;; ~2px taller due to the native input's UA line-height. Without the
    ;; bump the modal jumps 2px when swapping between searching / selected.
    [:div
     {:class (uu/cm "py-[13px] px-4 flex items-center justify-between"
                    "border border-solid border-base-light-border-paler")}
     [:div
      {:class "flex items-center gap-2 min-w-0"}
      [:i {:class (uu/cm "fa fal" icon)}]
      [:div {:class "truncate"} title]]
     ;; "Change ✕" is more discoverable than a bare ✕: the text answers "how
     ;; do I pick a different one?" so the user doesn't have to guess the
     ;; icon-only control.
     [:div
      {:class (uu/cm "flex items-center gap-1.5 pl-4 shrink-0 cursor-pointer"
                     "text-base-regular-text-paler hover:text-base-regular-text")
       :on-click (fn [_] (on-clear!))}
      [:div (loc/tr :entry-criteria/change-selection)]
      [:i {:class "fal fa-times"}]]]))

(defn target-entity-types
  "The entity-types the search-selector should hit for this opportunity's
   pipeline. Mirrors the case dispatch in the original `select-target-view`.

   Investor target-profile flows (froppo / exit-opp / investor-only sales-opp)
   surface person/company in the picker — the underlying legal entity — and
   the orchestrator wraps the pick in an investor on the backend side."
  [{:keys [opportunity-entity-type pipeline]}]
  (let [target-asset-types (let [ats (:pipeline/target-asset-types pipeline)]
                             (if (empty? ats) [:entity.type/company] ats))
        investor-only? (= (set (:pipeline/target-asset-types pipeline))
                          #{:entity.type/investor})]
    (case opportunity-entity-type
      :entity.type/fundraising-opportunity
      [:entity.type/company :entity.type/person]

      :entity.type/exit-opportunity
      [:entity.type/company :entity.type/person]

      :entity.type/sales-opportunity
      (if investor-only?
        [:entity.type/company :entity.type/person]
        (vec target-asset-types))

      :entity.type/oppo
      (vec target-asset-types)

      nil)))

(defn simple-view
  "Minimal type-then-entity picker, modelled after the nev
   `entity-relation-module` Add modal opened from a target-asset row: when
   the pipeline accepts more than one target-asset-type, the user first picks
   the type from a select; then picks (or creates) a specific entity of that
   type via an OpenSearch-backed select. Single-type pipelines skip straight
   to the entity select. Confirm commits the local selection as the
   opportunity's target; Cancel / X abort without touching the backend.

   The entity select's inline `:create!` (offered as a `+ Create new …`
   option inside the dropdown) replaces `view`'s separate +New buttons row."
  [{:keys [opportunity pipeline type-label search-label local-state
           on-create-new! on-confirm! on-cancel!]}]
  (let [opp-type (:entity/type opportunity)
        ets (target-entity-types {:opportunity-entity-type opp-type
                                  :pipeline pipeline})
        multi-type? (cu/multiple? ets)
        single-type (when-not multi-type? (first ets))
        selected-type (or single-type
                          (:selected-entity-type @local-state))
        selected-id (:selected-entity-id @local-state)
        selected-label (:selected-entity-label @local-state)
        selected-entity (some-> selected-id session/get-entity)
        select-entity! (fn [entity-id entity-label]
                         (if entity-id
                           (swap! local-state assoc
                                  :selected-entity-id entity-id
                                  :selected-entity-label entity-label)
                           (swap! local-state dissoc
                                  :selected-entity-id :selected-entity-label)))]
    [:div
     {:class "flex flex-col gap-3"}
     (when-not selected-type
       [labelled-input/view
        {:label type-label
         :data-type :data.type/keyword
         :value selected-type
         :mandatory? true
         :options (mapv (fn [et] {:value et :label (loc/tr et)}) ets)
         :on-change (fn [new-type]
                      (swap! local-state
                             (fn [s]
                               (-> s
                                   (assoc :selected-entity-type new-type)
                                   (dissoc :selected-entity-id)))))}])
     (when selected-type
       ^{:key (str selected-type)}
       [labelled-input/view
        {:label search-label
         :data-type :data.type/ref-single
         :editor-type :editor.type/search
         :mandatory? true
         :field/entity-types [selected-type]
         :value (when selected-id
                  {:value selected-id
                   ;; Prefer the label the async-select's own option already
                   ;; carried at selection time - the picked entity (e.g. a
                   ;; new-grid/OpenSearch search result) is not guaranteed to
                   ;; be in the legacy @session/entities cache the way an
                   ;; existing selection resolved from outside this component
                   ;; would be, and session/get-entity returning nil here
                   ;; used to silently blank the whole :value, making a real
                   ;; selection look like it never happened (see
                   ;; ai/mistakes.md).
                   :label (or selected-label
                              (some-> selected-entity entity/display-string))})
         :on-change (fn [val opt-val]
                      (select-entity! val (:label opt-val)))
         :create! (fn [query]
                    (on-create-new! selected-type query
                                    ;; On the new grid, a new entity is not in the session.
                                    ;; Its label must come with it. The search editor throws
                                    ;; an error for a value with no label.
                                    (fn [entity-id new-entity]
                                      (select-entity! entity-id
                                                      (or (some-> new-entity entity/display-string)
                                                          (loc/tr :loading))))))}])
     [:div
      {:class "flex justify-between w-full mt-2"}
      [button/secondary-hollow
       {:on-click (fn [_] (on-cancel!))}
       (loc/tr :cancel)]
      [button/primary-solid
       {:on-click (fn [_] (on-confirm! selected-id))
        :disabled? (nil? selected-id)}
       (loc/tr :confirm)]]]))

(defn view
  "Picker body with select-then-confirm semantics: clicking a search result
   or completing the `+ New` creator flow stores the entity as the local
   selection (`:selected-entity` in local-state); the user reviews the choice
   and explicitly commits via the footer's Confirm button. Cancel and the
   modal's X both abort without touching the backend.

   When something is selected the search input shows it as its value (via
   `search-selector/view`'s `:value` + `:on-clear` props); the results-area
   is left blank, so the user can re-engage the search by clicking the
   input's X. The create buttons stay clickable — using one is equivalent to
   abandoning the current selection in favor of a new entity.

   Layout uses fixed-height results-area + always-present create-buttons +
   always-present footer so the modal height stays stable across no-search /
   matched-results / selected states."
  [{:keys [opportunity pipeline search-label local-state
           on-create-new! on-confirm! on-cancel!]}]
  ;; `local-state` is the dynamic-modal's `state` cursor — backed by the
  ;; modal-system's `db/state` keyed by modal order, so it survives the brief
  ;; unmount/remount when the creator pops on top of this modal (dynamic-modal
  ;; uses `:visible? (and shown? topmost?)`). Without that persistence the
  ;; user's selection / typed query / cached results would all disappear when
  ;; they click `+ New X`.
  (r/with-let [searchbox-id "entry-criteria-target-search"
               query* (r/cursor local-state [:query])
               results-query* (r/cursor local-state [:results-query])
               results* (r/cursor local-state [:results])]
    (let [opp-type (:entity/type opportunity)
          ets (target-entity-types {:opportunity-entity-type opp-type
                                    :pipeline pipeline})
          results @results*
          search-done? (some? @results-query*)
          no-results? (and search-done? (empty? results))
          selected (:selected-entity @local-state)
          select-by-id! (fn
                          ([entity-id]
                           (when-let [e (some-> entity-id session/get-entity)]
                             (swap! local-state assoc :selected-entity e)))
                          ;; A new entity is not in the session on the new grid.
                          ;; Use the entity that the creator returned.
                          ([entity-id new-entity]
                           (when-let [e (or new-entity
                                            (some-> entity-id session/get-entity))]
                             (swap! local-state assoc :selected-entity e))))
          clear-selection! (fn []
                             (reset! query* nil)
                             (swap! local-state dissoc :selected-entity))]
      [:div
       {:class "flex flex-col gap-2"}
       (if selected
         [:div
          {:class "flex flex-col gap-1.5"}
          [labelled-input/label-elem {:label search-label}]
          [selected-card {:entity selected :on-clear! clear-selection!}]]
         [search-selector/view
          {:searchbox-id searchbox-id
           :label search-label
           :search-on-focus? true
           :on-focus #(swap! local-state assoc :expanded? true)
           :state local-state
           :placeholder search-label
           :entity-types ets}])
       [:div
        {:class (uu/cm "h-[235px] overflow-auto"
                       "border border-solid border-base-light-border-paler")}
        (when-not selected
          (cond
            no-results?
            [:div {:class "px-4 py-2"} (loc/tr :creator/no-results)]

            (seq results)
            [:div {:class "flex flex-col"}
             (doall
              (for [{:keys [id] :as result} results]
                ^{:key (str id)}
                [result-item
                 {:result result
                  :on-select select-by-id!}]))]))]
       [:div
        {:class "flex gap-2"}
        ;; One `+ New X` button per entity-type the pipeline accepts as a
        ;; target. `flex-1` distributes width evenly regardless of count,
        ;; so adding a fourth/fifth type doesn't need a width tweak.
        (doall
         (for [et ets
               :let [icon (entity/entity-type-icon et)
                     loc-key (get create-button-loc-keys et)]
               :when loc-key]
           ^{:key (str et)}
           [button/secondary-hollow
            {:class (uu/cm "bg-primary-light text-primary-regular flex-1"
                           "inline-block truncate flex min-w-0")
             :on-click (fn [_]
                         (on-create-new!
                          et
                          (initial-query-for-create et @query* @results*)
                          select-by-id!))}
            [:i {:class (uu/cm "fa" (name icon))}]
            (loc/tr loc-key)]))]
       [:div
        {:class "flex justify-between w-full mt-2"}
        [button/secondary-hollow
         {:on-click (fn [_] (on-cancel!))}
         (loc/tr :cancel)]
        [button/primary-solid
         {:on-click (fn [_] (on-confirm! (:db/id selected)))
          :disabled? (nil? selected)}
         (loc/tr :confirm)]]])))

(defn write-target-attribute!
  "Sets the opportunity's target-attribute to `target-id` and waits for the
   opportunity's `:common/updated-on` to advance before firing `on-done!`.
   The wait closes the sync gap between the backend acking the write and the
   new value reaching the session.

   `target-id` is whatever the target-attribute references — the legal entity
   id for direct-ref pipelines, the investor id for investor-target-profile
   pipelines."
  [{:keys [opportunity target-id on-done! on-error!]}]
  (let [opp-id (:db/id opportunity)
        opp-type (:entity/type opportunity)
        target-attr (pipeline/target-attribute opp-type)
        ts-before (some-> (session/get-entity opp-id) :common/updated-on)]
    (entity-actions/*->field-update!
     opp-id target-attr target-id
     (fn [_res]
       (session/wait-for-entity-update!
        opp-id ts-before
        (fn []
          (when (fn? on-done!)
            (on-done!)))))
     (fn [err]
       (when (fn? on-error!)
         (on-error! err))))))

(defn set-opportunity-target!
  "Writes `picked-id` as the opportunity's target-attribute. `picked-id` may
   be a legal entity (person/company) OR an investor — the backend's
   `ref-normalization/normalize-ref-single` inspects the target field's
   `:field/entity-types`:

   - Field expects an investor and the picked id is a legal entity →
     `normalize-investor-ref` reuses the existing active investor for that
     legal entity or creates a fresh one with the correct `:entity/type`.
   - Picked id is already an investor (e.g. the user just created one via
     the investor creator) → passes through unchanged.

   So the frontend doesn't need a separate `:entity/upsert-investor`
   round-trip or investor-target-profile branching here — one straight
   attribute write handles both."
  [{:keys [opportunity picked-id on-done! on-error!]}]
  (write-target-attribute! {:opportunity opportunity
                            :target-id picked-id
                            :on-done! on-done!
                            :on-error! on-error!}))

(defn open!
  "Open the no-target picker for `opportunity`. The user picks an existing
   entity from the search results or creates a new one via the creator; the
   choice is local until they click Confirm, which fires the backend target-
   write, waits for sync, closes the picker, and calls `:on-target-set!` so
   the caller can re-enter its flow (typically `attempt-move-to-stage!`).
   Cancel and the X both abort without touching the backend.

   `:target-pipeline` (optional) — change-pipeline flow override. When set,
   target-asset-types, investor-target-profile detection, and the eventual
   target-attribute write all resolve against this pipeline, since that's
   the pipeline the opportunity is moving into.

   `:open-creator!` is `frontend.view.entity/open-creator!`, passed in so this
   ns doesn't require it (avoiding a cycle — `frontend.view.entity` already
   requires this ns)."
  [{:keys [opportunity target-pipeline open-creator!
           on-target-set! on-cancel! on-error!]}]
  (let [opp-type (:entity/type opportunity)
        pipeline (if target-pipeline
                   (some-> (or (:db/id target-pipeline) target-pipeline)
                           session/get-entity)
                   (some-> (get opportunity (pipeline/pipeline-key opp-type))
                           session/get-entity))
        loc-keys (get no-target-loc-keys opp-type)
        stage-id (some-> opportunity (get (pipeline/stage-key opp-type)))
        stage (some-> stage-id session/get-entity)]
    (modals/open-modal!
     dynamic-modal/modal-key
     {:title (loc/tr (:title loc-keys))
      :show-button? false
      :on-close! (fn []
                   (when (fn? on-cancel!)
                     (on-cancel!)))
      :render-fn
      (fn [state close-modal!]
        (let [continue! (fn []
                          (close-modal!)
                          (when (fn? on-target-set!)
                            (on-target-set!)))
              fail! (fn [err]
                      (close-modal!)
                      (when (fn? on-error!)
                        (on-error! err)))
              cancel! (fn []
                        (close-modal!)
                        (when (fn? on-cancel!)
                          (on-cancel!)))]
          [simple-view
           {:opportunity opportunity
            :pipeline pipeline
            :type-label (loc/tr (:type-label loc-keys))
            :search-label (loc/tr (:search-label loc-keys))
            :local-state state
            :on-create-new!
            (fn [entity-type initial-query on-created!]
              ;; Even on investor-target-profile pipelines (froppo,
              ;; exit-oppo, sales-oppo on investor-only) we open the
              ;; plain person/company creator here — routing through the
              ;; investor creator would re-ask the user to select a
              ;; legal-entity type they just picked in this modal.
              ;; Backend ref-normalization wraps the returned legal-entity
              ;; id in an investor on the target-attribute write (reusing
              ;; an existing active investor when one is already linked
              ;; to that legal entity).
              (open-creator!
               entity-type
               {:prevent-open-entity-after-create? true
                :skip-success-msg? true
                :pipeline pipeline
                :stage stage
                :start-value initial-query
                :on-ok! (fn [{:keys [new-entity-id new-entity close-cb!]}]
                          (close-cb!)
                          (when (fn? on-created!)
                            (on-created! new-entity-id new-entity)))}))
            :on-confirm!
            (fn [picked-id]
              (set-opportunity-target!
               {:opportunity opportunity
                :picked-id picked-id
                :on-done! continue!
                :on-error! fail!}))
            :on-cancel! cancel!}]))})))
